import React, { PropsWithChildren, useState } from 'react';
import { $eq } from '../filters';
import { camelCase, startCase } from 'lodash';
import { EntityNameColumn } from '@components/entity-name-column';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';
import { Avatar, Divider, Tooltip, Typography } from '@material-ui/core';
import { useMemo } from 'react';
import CopyClipboard from '@material-ui/icons/AttachFile';
import { TimeAgo } from '@components/time-ago';

import { useCompanyLoader } from 'services/company-dataloader.service';
import { AvatarGroup, Skeleton } from '@material-ui/lab';
import CategoryIcon from '@material-ui/icons/Category';
import LanguageIcon from '@material-ui/icons/Language';
import DomainIcon from '@material-ui/icons/Domain';
import {
  DealDetail,
  DealEntity,
  dealHasOne,
  EntityType,
  getContentNameType,
  getEntityOrContentName,
} from '@lib/deals';
import { usePersonLoader } from 'services/person-dataloader.service';
import { useContentLoader } from 'services/content-dataloader.service';
import { isValid } from 'date-fns';
import EventIcon from '@material-ui/icons/Event';
import { EntityIcon } from '@components/entity-type-icon';
import { CellProps } from 'react-table';
type EnumObject = { label: React.ReactNode; value: any };
type EnumMap = EnumObject[];
import { orange, blue, deepOrange, pink, indigo, teal, cyan } from '@material-ui/core/colors';
import { makeStyles, createStyles, Theme, withStyles } from '@material-ui/core/styles';
import { ListItem, ListItemAvatar, ListItemText, Grid } from '@material-ui/core';
import CompanyDefaultIcon from '@icon/company-default';
import { readableDate, toPercentage, YmdStrToDate } from '@lib/common';
import { format, compareAsc } from 'date-fns';
import formatDistance from 'date-fns/formatDistance';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import { useDefaultImage } from '@lib/common';
import { uniq, uniqBy } from 'lodash';
import { ColoredAvatar, ColoredChip } from '@components/colored-elements';
import { windowObj } from '@lib/common';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import { Link } from '@material-ui/core';

/**
 * Convert a string based enum into objects
 * and redable labels.
 *
 * @param enum_ The enum to convert
 */
export const createEnumMap = (enum_: Record<string, any>, retainSpecialChars = false): EnumMap =>
  Object.values(enum_).map(value => ({
    label: retainSpecialChars ? value : startCase(camelCase(value)),
    value,
  }));

/**
 * Render a enum map to Select options.
 * @param enumMap The Enum map
 */
export const EnumsToOptions = (enumMap: EnumMap) => (
  <>
    {enumMap.map(v => (
      <option key={v.value} value={v.value}>
        {v.label}
      </option>
    ))}
  </>
);

/**
 * Override equals operator to show readable
 * values of enums.
 */
export const enumOperatorOverride = {
  [$eq.key]: {
    ChipLabel({ label, value }) {
      return (
        <>
          {label} is {startCase(camelCase(value))}
        </>
      );
    },
  },
};

/**
 * Create a small subtitle, used for Entity Names.
 */
function SmallSubTitle({ name, icon, display = 'block' }) {
  return (
    <span style={{ color: 'inherit', fontSize: 11, display, alignItems: 'center' }}>
      {icon}&nbsp;{name}
    </span>
  );
}

const skeleton = <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />;
/**
 * Gets the company details from the server and shows the
 * country and the primary type of the company.
 */
function ShowCompanyDetails({ id }: { id: string }) {
  const { loading, data: company, error } = useCompanyLoader(id);
  const companyType = company && company.company_types[0]?.name;
  return (
    <>
      {loading && (
        <>
          {skeleton}
          {skeleton}
        </>
      )}
      {!loading && company && (
        <>
          <SmallSubTitle
            name={company.hqcountry}
            icon={<LanguageIcon style={{ fontSize: '1em' }} />}
          />
          {companyType && (
            <SmallSubTitle
              name={companyType}
              icon={<CategoryIcon style={{ fontSize: '1em' }} color="primary" />}
            />
          )}
        </>
      )}
    </>
  );
}

/**
 * Gets the person details from the server.
 */
function ShowPersonDetails({ id }: { id: string }) {
  const { loading, data: person, error } = usePersonLoader(id);
  const personDepartment = person && person.role_id;
  return (
    <>
      {loading && (
        <>
          <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />
        </>
      )}
      {!loading && person && (
        <>
          <SmallSubTitle
            name={personDepartment}
            icon={<DomainIcon style={{ fontSize: '1em' }} />}
          />
        </>
      )}
    </>
  );
}

function ShowContentDetails({ id }: { id: string }) {
  const { loading, data: content, error } = useContentLoader(id);
  const finished = !loading && content;
  const release_date = finished && new Date(content.release_date);
  const year = finished && isValid(release_date) ? release_date.getFullYear() : 'NA';

  return (
    <>
      {loading && (
        <>
          <Skeleton style={{ display: 'block' }} variant="text" animation="wave" />
        </>
      )}
      {finished && (
        <>
          {content.content_type && (
            <>
              <SmallSubTitle
                display="inline-flex"
                name={content.content_type}
                icon={<CategoryIcon style={{ fontSize: '1em' }} color="primary" />}
              />
              &nbsp;
            </>
          )}
          {content.lang_names && (
            <>
              <SmallSubTitle
                display="inline-flex"
                name={content.lang_names}
                icon={<LanguageIcon style={{ fontSize: '1em' }} />}
              />
              &nbsp;
            </>
          )}
          {year && (
            <SmallSubTitle
              display="inline-flex"
              name={year}
              icon={<EventIcon style={{ fontSize: '1em' }} />}
            />
          )}
        </>
      )}
      {error && <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />}
    </>
  );
}

export const SingleBuyerSellerCell = ({
  name: entityName,
  vtrId: entityVtrId,
  type: entityType,
  white = false,
}: {
  name: React.ReactNode;
  vtrId?: string;
  type: string;
  white?: boolean;
  contentType?: any; //ignore
}) => {
  const secondaryProps = useMemo(() => (white ? { color: 'inherit' } : {}), [white]);
  let Details: any;

  if (entityType === EntityType.company) {
    Details = ShowCompanyDetails;
  } else if (entityType === EntityType.person) {
    Details = ShowPersonDetails;
  }
  return entityName || entityVtrId ? (
    <>
      <EntityNameColumn
        name={entityName}
        type={entityType}
        secondary={
          entityVtrId ? (
            Details ? (
              <Details id={entityVtrId} />
            ) : (
              <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
            )
          ) : null
        }
        secondaryTypographyProps={secondaryProps}
      />
    </>
  ) : null;
};

export const SingleContentCell = ({
  name,
  vtrId = null,
  white = false,
  contentType = null,
}: {
  name: React.ReactNode;
  vtrId?: string;
  white?: boolean;
  contentType?: string;
}) => {
  const secondaryProps = useMemo(() => (white ? { color: 'inherit' } : {}), [white]);
  return name ? (
    <EntityNameColumn
      name={name}
      type="CONTENT"
      secondary={vtrId ? <ShowContentDetails id={vtrId} /> : null}
      secondaryTypographyProps={secondaryProps}
      overrideAvatar={contentType}
    />
  ) : null;
};

export const CellForMultiEntity =
  (type: 'buyer' | 'seller' | 'content') =>
  (props: PropsWithChildren<CellProps<DealDetail, DealEntity[]>>) => {
    const entities = props.value || [];

    const RenderComponent = type === 'content' ? SingleContentCell : SingleBuyerSellerCell;

    if (!entities.length) {
      return null;
    }

    const firstEntityName = getEntityOrContentName(entities[0]);
    const contentType = type === 'content' ? getContentNameType(entities[0]) : null;

    return dealHasOne(type, props.row.original) ? (
      <RenderComponent
        name={firstEntityName}
        vtrId={entities[0].vtr_id}
        type={entities[0].type}
        contentType={contentType}
      />
    ) : (
      <>
        <RenderComponent
          name={
            <>
              {firstEntityName}{' '}
              <Typography color="textSecondary" variant="body2">
                +{entities.length - 1}
              </Typography>
            </>
          }
          contentType={contentType}
          type={entities[0].type}
        />

        <AvatarGroup>
          {entities.slice(1).map((entity, idx) => (
            <Avatar key={`entity-${idx}`} style={smallAvatar}>
              <Tooltip
                title={
                  <RenderComponent
                    name={getEntityOrContentName(entity)}
                    vtrId={entity.vtr_id}
                    type={entity.type}
                    white
                    contentType={getContentNameType(entity)}
                  />
                }>
                <EntityIcon noTooltip type={getContentNameType(entity) || entity.type} />
              </Tooltip>
            </Avatar>
          ))}
        </AvatarGroup>
      </>
    );
  };
const smallAvatar = { height: '1.1em', width: '1.1em' };

/**
 * Higher order function to create c
 * @param entity The entity to create object for.
 */
export const cellForEntity =
  (entity: 1 | 2 | '1' | '2') =>
  ({ row }) => {
    const entityName = row.original[`entity${entity}`];
    const entityVtrId = row.original[`entity${entity}_vtr_id`];
    const entityType = row.original[`entity${entity}_type`];
    return <SingleBuyerSellerCell name={entityName} vtrId={entityVtrId} type={entityType} />;
  };
/**
 *  function to create cell for content
 * @param entity The entity to create object for.
 */
export const CellForContent = ({ row }) =>
  row.original.content ? (
    <SingleContentCell name={row.original.content} vtrId={row.original.content_vtr_id} />
  ) : null;

/**
 *  function to create cell for content
 * @param entity The entity to create object for.
 */
export const CellForDealSource = ({ row }) =>
  row.original.deal_source && (
    <p>{row.original.deal_source.deal_source_type + 'xyz'}</p>
    // <EntityNameColumn
    //   name={row.original.content}
    //   type="CONTENT"
    //   secondary={
    //     row.original.content_vtr_id ? (
    //       <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
    //     ) : null
    //   }
    // />
  );

export const QuickTitle = ({ title, subTitle = 'Advanced Filters' }) => (
  <>
    <Typography variant="h5">{title}</Typography>
    <Typography variant="body2" color="textSecondary">
      {subTitle}
    </Typography>
    <br />
  </>
);

export const addTitleToFilter =
  (Filter: any, title?: string, subTitle = 'Advanced Filters') =>
  props => {
    const actualTitle = useMemo(
      () => title || props.column.render('Header'),
      [props.column.Header]
    );
    return (
      <>
        <QuickTitle title={actualTitle} subTitle={subTitle} />
        <Filter {...props} />
      </>
    );
  };

export const CellForLastUpdatedAt = ({ value, row }) => {
  return <TimeAgo time={value ? new Date(value) : new Date(row.original.created_at)} breakLine />;
};

export const CellForCount = (row, count_param) => {
  const paramsMap = {
    deals_count: {
      head: 'Deals',
      href: `${window.location.origin}/deals/search/?filters[0].id=entity_id&filters[0].value.$eq=${row.original.vtr_id}`,
    },
    company_count: {
      head: 'Companies',
      href: `${window.location.origin}/company-person/search?filters[0].id=vtr_id&filters[0].value.$eq=${row.original.vtr_id}&filters[1].id=currently_working&filters[1].value.$notlike=no`,
    },
    content_count: {
      head: 'Contents',
      href: `${window.location.origin}/content/search?filters[0].id=vtr_id&filters[0].value.$contains=${row.original.vtr_id}`,
    },
  };
  return (
    row.original && (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {
          <Tooltip title={`${paramsMap?.[count_param]?.head}: ${row.original?.[count_param]}`}>
            <Link href={paramsMap?.[count_param]?.href} target="_blank">
              <Typography color="primary" variant="h6">
                {row.original?.[count_param]}
              </Typography>
            </Link>
          </Tooltip>
        }
      </div>
    )
  );
};

export const AddNonLinkedEntityFilter = (Filter: any) => props => {
  return (
    <>
      <Filter {...props} addNonLinkedEntityFilter={true} />
    </>
  );
};

export const AddAvailableFilter = (Filter: any) => props => {
  return (
    <>
      <Filter {...props} addAvailableFilter={true} />
    </>
  );
};
/////////////////////////////////
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1),
      },
    },
    small: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    large: {
      width: theme.spacing(7),
      height: theme.spacing(7),
    },
    orange: {
      color: theme.palette.getContrastText(deepOrange[500]),
      backgroundColor: deepOrange[500],
    },
    contentImg: {
      width: '40px',
      height: '75px',
      marginRight: '5px',
    },
    containedImage: {
      '& .MuiAvatar-img': {
        objectFit: 'contain',
        // border: '1px solid lightgray',
        // backgroundColor: 'lightgray',
      },
    },
  })
);

export const CellForContentTitle = ({ value, row }) => {
  const classes = useStyles();
  const srcList = useMemo(() => {
    return [
      `${windowObj.RAZZLE_S3_BUCKET_POSTER_URL}/${row.original.content.vtr_id}.png`,
      `${windowObj.RAZZLE_TMDB_IMAGE_URL}/${row.original.content.poster_path}`,
      '/images/content_default.svg',
    ].filter(e => e);
  }, []);

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/content_default.svg',
    useSuspense: false,
  });

  return (
    value && (
      <Tooltip title={''}>
        <ListItem style={{ padding: 0 }} component="div">
          <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 6 }}>
            <Avatar
              variant="square"
              alt={`${row.original.content.title || row.original.content.original_title}`}
              src={fallbackedSource}
              className={[classes.containedImage, classes.contentImg].join(' ')}
            />
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography variant="body2">
                {row.original.content.title || row.original.content.original_title}
              </Typography>
            }
            secondary={
              row.original.content.vtr_id ? (
                <ShowContentDetails id={row.original.content.vtr_id} />
              ) : null
            }
          />
        </ListItem>
      </Tooltip>
    )
  );
};

export const CellForCompanyName =
  (type: 'company' | 'client') =>
  ({ value, row }) => {
    const classes = useStyles();
    const srcList = useMemo(() => {
      return [
        `${windowObj.RAZZLE_S3_BUCKET_URL}/${
          type === 'company' ? row.original.company_vtr_id : row.original.client_vtr_id
        }.png`,
        row.original.person_logo
          ? `${windowObj.RAZZLE_TMDB_IMAGE_URL}${
              type === 'company' ? row.original.company.logo_url : row.original.client.logo_url
            }`
          : '',
        '/images/company_default.svg',
      ].filter(e => e);
    }, []);

    const { src: fallbackedSource } = useDefaultImage({
      srcList,
      defaultSrc: '/images/company_default.svg',
      useSuspense: false,
    });

    return (
      value && (
        <Tooltip title={''}>
          <ListItem style={{ padding: 0 }} component="div">
            <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 6 }}>
              <Avatar
                alt={`${
                  type === 'company'
                    ? row.original.company.vtr_company_name
                    : row.original.client.vtr_company_name
                }`}
                src={fallbackedSource}
                className={[
                  classes.containedImage,
                  // , classes.large
                ].join(' ')}
              />
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="body2">{`${
                  type === 'company'
                    ? row.original.company.vtr_company_name
                    : row.original.client.vtr_company_name
                }`}</Typography>
              }
              secondary={
                row.original.company.vtr_id ? (
                  <ShowCompanyDetails id={row.original.company.vtr_id} />
                ) : row.original.client.vtr_id ? (
                  <ShowCompanyDetails id={row.original.client.vtr_id} />
                ) : null
              }
            />
          </ListItem>
        </Tooltip>
      )
    );
  };

// export const CellForCompanyPersons = ({ row }) => {
//   const max_limit: number = 2;
//   const list_of_company_services = row.original.company_service_name
//     ? [row.original.company_service_name]
//     : [];
//   const title = list_of_company_services
//     .slice(max_limit)
//     .map(eachCompanyService => eachCompanyService['name'])
//     .join(', ');
//   const sliced = list_of_company_services.slice(0, max_limit);
//   return (
//     row.original.company_service_name && (
//       <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
//         {sliced.map((eachCompanyService, index) => {
//           return (
//             <div key={index} style={{ display: 'flex' }}>
//               <Tooltip title={eachCompanyService}>
//                 <ColoredChip
//                   colored={blue[500]}
//                   size="small"
//                   // variant="outlined"
//                   label={eachCompanyService}
//                 />
//               </Tooltip>
//               {index === sliced.length - 1 && list_of_company_services.length - max_limit > 0 ? (
//                 <>
//                   &nbsp;
//                   <Tooltip title={title}>
//                     <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
//                       list_of_company_services.length - max_limit
//                     }`}</span>
//                   </Tooltip>
//                 </>
//               ) : null}
//             </div>
//           );
//         })}
//       </div>
//     )
//   );
// };

export const cellForPersonaka = ({ value, row }) => {
  const max_limit: number = 2;
  const listOfOtherServiceAkas = value && value ? uniq(value.split(';;').map(x => x.trim())) : [];

  const title = listOfOtherServiceAkas.slice(max_limit).join(', ');
  const sliced = listOfOtherServiceAkas.slice(0, max_limit);

  return (
    value && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
        {sliced.map((eachServiceAka, index) => {
          return (
            <div key={index} style={{ display: 'flex' }}>
              <Tooltip title={eachServiceAka}>
                <ColoredChip
                  colored={teal[500]}
                  size="small"
                  // variant="outlined"
                  label={eachServiceAka}
                />
              </Tooltip>
              {index === sliced.length - 1 && listOfOtherServiceAkas.length - max_limit > 0 ? (
                <Tooltip title={title}>
                  <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                    listOfOtherServiceAkas.length - max_limit
                  }`}</span>
                </Tooltip>
              ) : null}
            </div>
          );
        })}
      </div>
    )
  );
};

export const cellForPersonPublish = ({ value, row }) => {
  return value == true ? (
    <Grid container spacing={2} alignItems="center">
      <Grid item>
        <Tooltip title="Published">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon style={{ fontSize: '2.5rem', paddingRight: '5px', color: 'green' }} />
            {/* <Typography variant="body2" component="h6">
              TV Series
            </Typography> */}
          </div>
        </Tooltip>
      </Grid>
    </Grid>
  ) : (
    <Grid container spacing={2} alignItems="center">
      <Grid item>
        <Tooltip title="Un-Published">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <LocalOfferIcon style={{ fontSize: '2.5rem', paddingRight: '5px', color: 'yellow' }} />
            {/* <Typography variant="body2" component="h6">
              Movie
            </Typography> */}
          </div>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export const cellForPersonString = ({ value, row }) => {
  return <Typography component="h6">{value}</Typography>;
};

export const cellForPersonSource = ({ value, row }) => {
  let source =
    value == 1001
      ? 'TMDB'
      : value == 2001
      ? ' OMDB'
      : value == 3001
      ? '  GVD '
      : value == 4001
      ? 'DELI'
      : value == 6001
      ? 'HUBSPOT'
      : 'NA';
  return <Typography component="h6">{source}</Typography>;
};

export const resolvePersonDisplayName = (person: any): string => {
  const parts = [
    person?.firstname,
    person?.middlename,
    person?.lastname,
  ]
    .map(p => p?.trim())
    .filter(Boolean);

  if (parts.length > 0) {
    return parts.join(' ');
  }

  return person?.name?.trim() || '';
};

export const cellForPersonName = ({ value, row }) => {
  const classes = useStyles();

  const displayName = useMemo(() => {
    return resolvePersonDisplayName(row.original);
  }, [row.original]);

  const srcList = useMemo(() => {
    return [
      `${windowObj.RAZZLE_PERSON_LOGO_PROFILE}/${row.original.vtr_id}.png?ts=${encodeURIComponent(row.original.modified_at)}`,
      `${windowObj.RAZZLE_TMDB_IMAGE_URL}${row.original.profile_path}`,
      '/images/content_default.svg',
    ].filter(Boolean);
  }, [row.original]);

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/content_default.svg',
    useSuspense: false,
  });

  if (!displayName) return null;

  return (
    <Tooltip title={displayName}>
      <ListItem style={{ padding: 0 }} component="div">
        <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 2 }}>
          <Avatar
            alt={displayName}
            src={fallbackedSource}
            variant="square"
            className={classes.containedImage}
          />
        </ListItemAvatar>

        <ListItemText
          primary={
            <Typography variant="body2">
              <ClipBoard
                parentValue={displayName}
                icon={<CopyClipboard style={{ fontSize: '1.2em' }} color="primary" />}
                value={row.original.vtr_id}
                tooltipTitle={"Copy the Vitrina Id"}
              />
            </Typography>
          }
        />
      </ListItem>
    </Tooltip>
  );
};

export default function ClipBoard({ parentValue, icon, display = 'block', value,tooltipTitle='Copy the Vitrina Id' }) {
  const [isHovering, setHovering] = useState(false);
  const [isClick, setClick] = useState(false);
  return (
    <>
      <span
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => {
          setClick(false);
          setTimeout(() => {
            setHovering(false);
          }, 5000);
        }}>
        {parentValue}
      </span>
      <span
        style={{
          color: 'inherit',
          fontSize: 11,
          display: isHovering ? 'inline' : 'none',
          alignItems: 'center',
          paddingTop: '2px',
        }}>
        <Tooltip
          title={isClick ? 'Copied!!' : tooltipTitle}
          // arrow
          // followCursor
          onClick={() => {
            navigator.clipboard.writeText(value);
            setClick(true);
          }}>
          {icon}
        </Tooltip>
      </span>
    </>
  );
}
